import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  CheckCircle2,
  ArrowRight,
  Phone,
  Users,
  Handshake,
  Target,
  Search,
  FileCheck,
  RefreshCw,
  BarChart3,
  Award,
  TrendingUp,
  Link2
} from 'lucide-react';

export const RetainerPlanPage = () => {
  const coverage = [
    {
      icon: Handshake,
      title: 'Sourcing & Partnership',
      lede: 'Your supply and distribution backbone needs to stay strong as you grow, not just at the start. Your expert keeps a live pipeline of vetted importers, distributors, and GMP-certified manufacturers, steps in when a partner underperforms or a new market needs local representation, and manages agreements and GMP or new-site applications as they arise.',
      outcome: 'You always have the right partner in the right market, and never scramble to find one.'
    },
    {
      icon: Target,
      title: 'Groundwork & Market Intelligence',
      lede: 'Every new launch should be decided before it begins. Your expert continuously scores new country and SKU opportunities against demand and regulatory complexity, maps the registration pathway for each, and builds the market-entry roadmap, so expansion is a planned move, not a leap.',
      outcome: 'You enter the right markets, with the right products, in the right order, every time.'
    },
    {
      icon: Search,
      title: 'Gap Analysis',
      lede: "Dossiers drift out of compliance as rules change and portfolios grow. Rather than checking documents once, your expert keeps them continuously mapped against each country's checklist, tracks corrective actions to closure, and maintains a live readiness scorecard across your portfolio.",
      outcome: 'Your dossiers are always submission-ready, so a new opportunity never waits on paperwork.'
    },
    {
      icon: FileCheck,
      title: 'Registration & Dossier',
      lede: "When it is time to file, the work is already underway. Your expert prepares full CTD/ACTD dossiers to each country's exact format, makes artwork and labelling compliant, handles certified translations, and drafts fast, evidence-backed responses to any regulator query or deficiency letter.",
      outcome: 'Clean submissions that clear faster, and queries answered before they become delays.'
    },
    {
      icon: RefreshCw,
      title: 'Lifecycle Support',
      lede: 'Winning a registration is only half the job; keeping it is the other half. Your expert tracks every renewal well ahead of expiry, prepares variation dossiers for formulation, artwork, site, or pack-size changes, manages post-approval changes and audit readiness, and keeps an open line with each authority.',
      outcome: 'Zero registrations lost to a missed deadline, and no market access interrupted.'
    },
    {
      icon: BarChart3,
      title: 'Monthly Compliance Reporting',
      lede: 'You stay fully in control without chasing updates. Each month you receive a consolidated status report covering completed work, open items, upcoming deadlines across every market, and any risks flagged early enough to act on.',
      outcome: 'A clear, current view of your entire regulatory portfolio, on one page, every month.'
    }
  ];

  const processSteps = [
    'We understand your products, markets, and current regulatory position',
    'We assign your dedicated regulatory affairs expert',
    'Your expert manages every stage continuously, across all your markets',
    'You receive monthly compliance reporting and stay in control throughout'
  ];

  const idealFor = [
    'Exporters managing regulatory work across multiple markets at once',
    'Manufacturers expanding into new countries on a rolling basis',
    'Companies that need regulatory capability without the cost of an in-house team',
    'Growth-focused businesses that want one accountable expert, not a rotating cast'
  ];

  const faqs = [
    {
      q: 'What is the Apostrophe Retainer?',
      a: 'The Apostrophe Retainer is an ongoing engagement that assigns one dedicated regulatory affairs expert to manage your entire regulatory workflow. It brings the full scope of our five Success Packs together under continuous management, so pharma and nutra exporters get a regulatory department on call without hiring an in-house team.'
    },
    {
      q: 'How is the retainer different from the individual Success Packs?',
      a: 'The Success Packs are project-based engagements for a specific stage, such as gap analysis or dossier preparation. The retainer delivers all of that continuously, through one expert who knows your products and history, so there is no repeated onboarding or lost context as your work moves from stage to stage and market to market.'
    },
    {
      q: 'What does the retainer cover?',
      a: 'It covers the full regulatory journey: sourcing and partnerships, market groundwork, gap analysis, registration and dossier preparation, artwork and translations, query handling, and lifecycle support including renewals and variations. It also includes monthly compliance reporting on your entire portfolio.'
    },
    {
      q: 'Who should choose the retainer instead of a single pack?',
      a: 'The retainer suits exporters managing regulatory work across several markets, manufacturers expanding into new countries on a rolling basis, and companies that need ongoing regulatory capability without the cost of building an internal team. If your regulatory work is continuous rather than one-off, the retainer fits.'
    },
    {
      q: 'Do I get a single point of contact?',
      a: 'Yes. You are assigned one dedicated regulatory affairs expert who manages every stage of your workflow, holds the full context of your portfolio, and remains your consistent point of contact throughout the engagement.'
    },
    {
      q: 'Does the retainer include ongoing reporting?',
      a: 'Yes. You receive monthly compliance reporting covering completed tasks, open items, upcoming deadlines, and flagged risks, so you always have a clear, current view of where every registration and submission stands.'
    }
  ];

  const serviceSchema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'The Apostrophe Retainer',
    'alternateName': 'Outsourced Regulatory Affairs Retainer for Pharma Exporters',
    'serviceType': 'Outsourced Regulatory Affairs Retainer',
    'description':
      'An ongoing regulatory affairs engagement that assigns one dedicated expert to manage sourcing, market groundwork, gap analysis, dossier preparation, translations, artwork compliance, query handling, lifecycle management and monthly compliance reporting for pharma and nutra exporters.',
    'provider': { '@type': 'Organization', 'name': 'TheApostrophe', 'url': 'https://www.theapostrophe.com' },
    'areaServed': ['Africa', 'GCC', 'Asia', 'Latin America'],
    'url': 'https://www.theapostrophe.com/retainer',
    'offers': {
      '@type': 'Offer',
      'availability': 'https://schema.org/InStock',
      'url': 'https://www.theapostrophe.com/retainer'
    }
  };

  const howToSchema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    'name': 'How the Apostrophe Retainer works',
    'description': 'A four-step process to hand your entire regulatory workflow to one dedicated expert.',
    'step': processSteps.map((s, i) => ({
      '@type': 'HowToStep',
      'position': i + 1,
      'name': `Step ${i + 1}`,
      'text': s
    }))
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': faqs.map((f) => ({
      '@type': 'Question',
      'name': f.q,
      'acceptedAnswer': { '@type': 'Answer', 'text': f.a }
    }))
  };

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    'itemListElement': [
      { '@type': 'ListItem', 'position': 1, 'name': 'Home', 'item': 'https://www.theapostrophe.com/' },
      { '@type': 'ListItem', 'position': 2, 'name': 'The Apostrophe Retainer', 'item': 'https://www.theapostrophe.com/retainer' }
    ]
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#F0E0D0' }}>
      <Helmet>
        <title>Outsourced Regulatory Affairs Retainer for Pharma Exporters</title>
        <meta
          name="description"
          content="Your regulatory department on call. One dedicated expert manages your entire pharma and nutra regulatory workflow across Africa, GCC, Asia and Latin America."
        />
        <meta
          name="keywords"
          content="outsourced regulatory affairs, regulatory affairs retainer, outsourced RA team pharma, dedicated regulatory affairs expert, ongoing regulatory management, pharma export regulatory partner, regulatory affairs consultancy retainer, in-house regulatory alternative"
        />
        <link rel="canonical" href="https://www.theapostrophe.com/retainer" />

        <meta property="og:title" content="Outsourced Regulatory Affairs Retainer for Pharma Exporters" />
        <meta property="og:description" content="Your regulatory department on call. One dedicated expert manages your entire pharma and nutra regulatory workflow." />
        <meta property="og:url" content="https://www.theapostrophe.com/retainer" />
        <meta property="og:type" content="website" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Outsourced Regulatory Affairs Retainer for Pharma Exporters" />
        <meta name="twitter:description" content="One dedicated regulatory affairs expert managing your entire workflow, continuously." />

        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="pt-32 pb-20 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <Badge className="mb-6 px-6 py-2" style={{ backgroundColor: '#A060B0', color: 'white' }} data-testid="retainer-badge">
            Regulatory Launch Program
          </Badge>
          <div className="w-24 h-24 rounded-2xl bg-white/60 flex items-center justify-center mb-6 mx-auto shadow-md">
            <Users className="h-12 w-12" style={{ color: '#A060B0' }} aria-hidden="true" />
          </div>
          <h1
            className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight"
            style={{ color: '#303040' }}
            data-testid="retainer-h1"
          >
            The Apostrophe Retainer: Your Regulatory Department on Call
          </h1>
          <p className="text-xl md:text-2xl font-bold mb-8" style={{ color: '#A060B0' }}>
            Outsource your entire regulatory workflow to one dedicated expert.
          </p>
          <p className="text-base md:text-lg mb-10 max-w-3xl mx-auto leading-relaxed" style={{ color: '#606070' }}>
            Hiring, training, and retaining an in-house regulatory affairs team is slow and expensive, and most exporters do not have the volume to justify it. The Apostrophe Retainer gives you the same capability without the overhead. We assign one dedicated regulatory affairs expert to your company to manage your entire regulatory workflow on an ongoing basis, bringing the full scope of our individual packs together and delivering it continuously, rather than project by project. You get a regulatory department on call, without ever having to build one.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
                data-testid="retainer-discovery-btn"
              >
                Book a Discovery Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#A060B0', color: '#A060B0' }}
                data-testid="retainer-expert-btn"
              >
                Talk to a Regulatory Expert
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Why a Retainer */}
      <section className="py-20 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-8 text-center" style={{ color: '#303040' }}>
            Why a Retainer
          </h2>
          <p className="text-base md:text-lg leading-relaxed mb-6" style={{ color: '#606070' }}>
            Regulatory work is never one-and-done. A dossier leads to a submission, a submission leads to queries, an approval leads to renewals and variations, and a new market restarts the whole cycle. Handled pack by pack, that means repeated onboarding and lost context every time. Handled on a retainer, it means one expert who already knows your products, your markets, and your history, moving your entire portfolio forward without the stop-start.
          </p>
          <p className="text-base md:text-lg leading-relaxed italic" style={{ color: '#A060B0' }}>
            For most growing exporters, this is faster, more consistent, and far less costly than building an internal team.
          </p>
        </div>
      </section>

      {/* What the Retainer Covers */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            What the Retainer Covers
          </h2>
          <p className="text-base md:text-lg text-center mb-16 max-w-3xl mx-auto" style={{ color: '#606070' }}>
            Think of it as an entire regulatory function, held by one person who knows your business inside out. Instead of briefing a new consultant for every task, you have a single expert carrying your products, markets, and full history forward, every day, across every stage below.
          </p>
          <div className="space-y-10">
            {coverage.map((c, i) => (
              <article
                key={i}
                className="rounded-3xl p-8 md:p-12 shadow-md hover:shadow-xl transition-shadow"
                style={{ backgroundColor: 'white' }}
              >
                <div className="grid md:grid-cols-[auto_1fr] gap-8 items-start">
                  <div className="flex md:flex-col items-start gap-4 md:gap-3">
                    <div
                      className="w-16 h-16 rounded-2xl flex items-center justify-center flex-shrink-0"
                      style={{ backgroundColor: '#A060B0' }}
                    >
                      <c.icon className="h-8 w-8 text-white" aria-hidden="true" />
                    </div>
                    <div className="text-4xl font-bold" style={{ color: '#A060B0', opacity: 0.5 }}>
                      0{i + 1}
                    </div>
                  </div>
                  <div>
                    <h3 className="text-xl md:text-2xl font-bold mb-4" style={{ color: '#303040' }}>
                      {c.title}
                    </h3>
                    <p className="text-base md:text-lg leading-relaxed mb-5" style={{ color: '#606070' }}>
                      {c.lede}
                    </p>
                    <div className="p-4 rounded-xl flex items-start gap-3" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
                      <TrendingUp className="h-5 w-5 mt-1 flex-shrink-0" style={{ color: '#A060B0' }} aria-hidden="true" />
                      <p className="text-base leading-relaxed" style={{ color: '#505060' }}>
                        <span className="font-bold" style={{ color: '#A060B0' }}>The outcome:</span>{' '}
                        {c.outcome}
                      </p>
                    </div>
                  </div>
                </div>
              </article>
            ))}
          </div>

          {/* Continuity block */}
          <div className="mt-16 p-8 md:p-12 rounded-3xl text-center" style={{ backgroundColor: 'rgba(160, 96, 176, 0.12)' }}>
            <div className="flex items-center justify-center gap-3 mb-4">
              <Link2 className="h-7 w-7" style={{ color: '#A060B0' }} aria-hidden="true" />
              <h3 className="text-2xl md:text-3xl font-bold" style={{ color: '#A060B0' }}>
                One expert, complete continuity
              </h3>
            </div>
            <p className="text-base md:text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: '#505060' }}>
              Because the same person handles all of it, nothing falls between the stages. The context that would normally be lost each time you hand off from sourcing to dossier to renewal stays intact, and your regulatory function runs as one connected whole rather than a series of disconnected projects.
            </p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-16 text-center" style={{ color: '#303040' }}>
            How It Works
          </h2>
          <ol className="space-y-6">
            {processSteps.map((step, i) => (
              <li key={i} className="flex items-start gap-6 p-6 rounded-2xl" style={{ backgroundColor: 'white' }}>
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 font-bold text-white text-lg"
                  style={{ backgroundColor: '#A060B0' }}
                  aria-hidden="true"
                >
                  {i + 1}
                </div>
                <p className="text-base md:text-lg leading-relaxed pt-2" style={{ color: '#505060' }}>
                  {step}
                </p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Ideal For */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-center gap-3 mb-8">
            <Users className="h-8 w-8" style={{ color: '#A060B0' }} aria-hidden="true" />
            <h2 className="text-3xl md:text-4xl font-bold" style={{ color: '#303040' }}>
              Ideal For
            </h2>
          </div>
          <div className="grid md:grid-cols-2 gap-4 max-w-4xl mx-auto">
            {idealFor.map((item, i) => (
              <div key={i} className="flex items-start gap-3 p-6 rounded-xl" style={{ backgroundColor: '#F8F4EE' }}>
                <CheckCircle2 className="h-6 w-6 mt-0.5 flex-shrink-0" style={{ color: '#A060B0' }} aria-hidden="true" />
                <span className="text-base md:text-lg" style={{ color: '#505060' }}>
                  {item}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why This Matters */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="p-8 md:p-12 rounded-3xl" style={{ backgroundColor: 'rgba(160, 96, 176, 0.12)' }}>
            <div className="flex items-center gap-3 mb-6">
              <Award className="h-7 w-7" style={{ color: '#A060B0' }} aria-hidden="true" />
              <h2 className="text-2xl md:text-3xl font-bold" style={{ color: '#A060B0' }}>
                Why This Matters
              </h2>
            </div>
            <p className="text-lg md:text-xl leading-relaxed" style={{ color: '#505060' }}>
              A retainer turns regulatory affairs from a series of disconnected projects into a single, managed function. One expert holds the full picture, nothing falls between the gaps, and your team is freed to focus on the business while compliance runs smoothly in the background. It is the difference between reacting to regulatory work and staying ahead of it.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 px-6" style={{ backgroundColor: 'rgba(160, 96, 176, 0.08)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: '#303040' }}>
            Ready to Outsource Your Regulatory Workflow?
          </h2>
          <p className="text-lg mb-10" style={{ color: '#606070' }}>
            Tell us about your products and target markets. We will show you how a dedicated regulatory expert can manage your entire journey.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true"
              target="_blank"
              rel="noopener noreferrer external"
              onClick={(e) => {
                e.preventDefault();
                window.open('https://outlook.office.com/book/ScheduleaRegulatoryCall1@theapostrophe.in/?ismsaljsauthenabled=true', '_blank', 'noopener,noreferrer');
              }}
            >
              <Button
                size="lg"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ backgroundColor: '#A060B0', color: 'white' }}
                data-testid="retainer-cta-discovery-btn"
              >
                Book a Discovery Call
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
            <Link to="/contact#send-us-your-details">
              <Button
                size="lg"
                variant="outline"
                className="text-lg px-8 py-6 rounded-full transition-all duration-300 hover:scale-105"
                style={{ borderColor: '#A060B0', color: '#A060B0' }}
                data-testid="retainer-cta-meeting-btn"
              >
                Schedule a Meeting
                <Phone className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ - AEO */}
      <section className="py-24 px-6" style={{ backgroundColor: 'white' }}>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: '#303040' }}>
            Frequently Asked Questions
          </h2>
          <p className="text-center text-base md:text-lg mb-16" style={{ color: '#708080' }}>
            Straight answers about the Apostrophe Retainer engagement.
          </p>
          <div className="space-y-6">
            {faqs.map((f, i) => (
              <details
                key={i}
                className="group rounded-2xl p-6 md:p-8 transition-shadow hover:shadow-md"
                style={{ backgroundColor: '#F8F4EE' }}
                open={i === 0}
              >
                <summary
                  className="cursor-pointer list-none flex items-start justify-between gap-4"
                  data-testid={`retainer-faq-${i}`}
                >
                  <h3 className="text-lg md:text-xl font-bold" style={{ color: '#303040' }}>
                    {f.q}
                  </h3>
                  <span
                    className="w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center transition-transform group-open:rotate-45"
                    style={{ backgroundColor: '#A060B0', color: 'white' }}
                    aria-hidden="true"
                  >
                    +
                  </span>
                </summary>
                <p className="text-base md:text-lg leading-relaxed mt-4" style={{ color: '#606070' }}>
                  {f.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};
